import { AsyncPipe, DecimalPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { Chart, ChartConfiguration, Plugin } from 'chart.js'
import { BaseChartDirective } from 'ng2-charts'
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, map, Observable, ReplaySubject, shareReplay, switchMap, tap } from 'rxjs'
import { ButtonToggleComponent, ButtonToggleOption } from '../common/button-toggle/button-toggle.component'
import { convertIntervalKeyToTimeInterval, IntervalKey, TimeIntervalComponent } from '../common/time-interval/time-interval.component'
import { getActiveToggleMenuOptionIds } from '../common/toggle-menu/toggle-menu.component'
import { constructQueryParameterName, getPaginationOptionsFromQueryParamMap } from '../list-query-options'
import { AppTableColumn, TableComponent } from '../table/table.component'
import { environmentHue, EnvironmentPillComponent } from '../multi-environment/environment-pill/environment-pill.component'
import { FanOut, scopedId } from '../multi-environment/environment-merge'
import { qualifiedByEnvironment, SupportEnvironment } from '../multi-environment/support-environment'
import { SupportLinkResolver } from '../multi-environment/support-link.resolver'
import { SupportLinkComponent } from '../multi-environment/support-link/support-link.component'
import {
  AnalyticsDashboardService,
  AnalyticsScope,
  CompletedCallRow,
  MeetingRoomRow,
  UsageByOrganizationRow,
  UsageByUserRow,
} from './analytics-dashboard.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { constructNameForUser, getColorPalette, isEqual } from '../utils'
import { MatButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import DataLabelsPlugin from 'chartjs-plugin-datalabels'
import annotationPlugin from 'chartjs-plugin-annotation'
import { AUDIO_QUALITY_CHART_DATA, createDotsPlugin } from './audio-quality-chart.utils'

Chart.register(annotationPlugin)

const GOLDEN_ANGLE_DEGREES = 137.508

// A row whose organization is not among the ones being charted — a meeting belonging to one the
// selection has since dropped, say — still has to be drawn as something.
const UNKNOWN_ORGANIZATION_COLOR = '#B51800'

/**
 * What the panels show while the environments are answering. Rows appear as soon as any environment
 * has some, so a slow one does not hold the whole screen on its spinner; the error state is kept for
 * every environment having answered and none of them usefully.
 */
function loadState(result: FanOut<unknown[]>): { isLoading: boolean; isError: boolean } {
  return { isLoading: result.summary.isChecking && result.data.length === 0, isError: result.summary.hasNothingToShow }
}

@Component({
  selector: 'app-analytics-dashboard',
  standalone: true,
  imports: [
    TimeIntervalComponent,
    AsyncPipe,
    TranslatePipe,
    BaseChartDirective,
    SpinnerComponent,
    TableComponent,
    SupportLinkComponent,
    ButtonToggleComponent,
    DecimalPipe,
    EnvironmentPillComponent,
    MatButton,
    MatIcon,
    MatSlideToggle,
  ],
  templateUrl: './analytics-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './analytics-dashboard.component.scss',
})
export class AnalyticsDashboardComponent implements OnInit {
  /**
   * Scoped organization ids rather than the plain row ids, because the same number is a different
   * organization in every environment this reads from. See scopedId.
   */
  @Input({ required: true })
  organizationIds$!: Observable<string[]>

  /**
   * Which metrics routes to read. The support screens read the ones that answer for any organization;
   * a client's own dashboard reads the ones scoped to that client.
   */
  @Input()
  scope: AnalyticsScope = 'admin'

  @Input()
  dashboardUrl: string | null = null

  @Input()
  showAudioMetrics = false

  @Input()
  showMeetingRoomMetrics = false

  @Input()
  showDetailedAudioMetrics = false

  @Input()
  showCallNameToggle = false

  dataLabelsPlugin: Plugin<'bar'> = DataLabelsPlugin as Plugin<'bar'>

  showCallLabels$ = new BehaviorSubject<boolean>(false)
  showMeetingRoomLabels$ = new BehaviorSubject<boolean>(false)

  toggleCallLabels() {
    this.showCallLabels$.next(!this.showCallLabels$.value)
  }

  toggleMeetingRoomLabels() {
    this.showMeetingRoomLabels$.next(!this.showMeetingRoomLabels$.value)
  }

  ngOnInit() {
    this.organizationIds$.subscribe((value) => this._organizationIds$.next(value))
    // Built here rather than as a field: the cell templates are static view children, which are only
    // resolved once the view exists, and a column would silently fall back to plain text without one.
    this.meetingRoomColumns = this.buildMeetingRoomColumns()
  }

  _organizationIds$ = new ReplaySubject<string[]>(1)
  route = inject(ActivatedRoute)
  router = inject(Router)
  analyticsService = inject(AnalyticsDashboardService)
  translateService = inject(TranslateService)
  isMultiEnvironment = this.analyticsService.isMultiEnvironment
  completedCallQueryPrefix = 'completedCalls'
  usageStatsQueryPrefix = 'usageStats'
  defaultTimeInterval = 'LAST_6_MONTHS' as IntervalKey
  completedCallsDefaultPageSize = 25

  selectedInterval$ = this.route.queryParamMap.pipe(map((queryParamMap) => (queryParamMap.get('intervalKey') ?? this.defaultTimeInterval) as IntervalKey))

  interval$ = this.route.queryParamMap.pipe(
    map((params) => (params.get('intervalKey') as IntervalKey) ?? this.defaultTimeInterval),
    distinctUntilChanged(),
    map(convertIntervalKeyToTimeInterval),
    shareReplay(1)
  )

  usagesByOrganization$ = combineLatest([this._organizationIds$, this.interval$]).pipe(
    debounceTime(300),
    switchMap(([organizationIds, interval]) => this.analyticsService.usageByOrganization(this.scope, organizationIds, interval.from)),
    shareReplay(1)
  )

  usageSummary$ = this.usagesByOrganization$.pipe(
    map((result) => {
      const organizations = result.data
      const totals = organizations
        .flatMap((organization) => organization.usageByInterval)
        .reduce(
          (acc, interval) => ({
            duration: acc.duration + (interval.duration ?? 0),
            count: acc.count + (interval.count ?? 0),
          }),
          { duration: 0, count: 0 }
        )
      return {
        ...loadState(result),
        duration: totals.duration,
        count: totals.count,
        activeUserCount: organizations.reduce((acc, organization) => acc + organization.activeUserCount, 0),
      }
    }),
    shareReplay(1)
  )
  private usageStatsRequestOptions$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => ({
      intervalKey: (queryParamMap.get('intervalKey') as IntervalKey) ?? this.defaultTimeInterval,
    })),
    distinctUntilChanged(isEqual),
    shareReplay(1)
  )

  userStats$ = combineLatest([this.usageStatsRequestOptions$, this._organizationIds$]).pipe(
    map(([requestOptions, organizationIds]) => ({ ...requestOptions, organizationIds })),
    distinctUntilChanged(isEqual),
    switchMap(({ intervalKey, organizationIds }) =>
      this.analyticsService.usageByUser(this.scope, organizationIds, convertIntervalKeyToTimeInterval(intervalKey).from)
    ),
    map((result) => ({ ...loadState(result), data: result.data })),
    shareReplay(1)
  )

  usageStatsSelectedView$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => queryParamMap.get(constructQueryParameterName('activeView', this.usageStatsQueryPrefix)) ?? 'HOURS'),
    distinctUntilChanged(),
    tap((view) => console.log('Usage stats view changed to', view)),
    shareReplay(1)
  )

  /**
   * One column per interval, named after the interval itself rather than by a translation key, and
   * shown in whichever unit the view is set to.
   */
  columnDefinitionsForUserStats$ = combineLatest([this.userStats$, this.usageStatsSelectedView$, this._organizationIds$]).pipe(
    map(([result, viewMode, organizationIds]): AppTableColumn<UsageByUserRow>[] => {
      const usageData = result.data
      if (usageData.length === 0) {
        return []
      }

      const readUsage = (value: number | null): number => (value === null ? -1 : viewMode === 'HOURS' ? value / 3600 : value)
      const showUsage = (value: number | null): string => {
        if (value === null) {
          return 'N/A'
        }
        if (value === 0) {
          return '-'
        }
        return viewMode === 'HOURS' ? (value / 3600).toFixed(1) : String(value)
      }

      const name: AppTableColumn<UsageByUserRow> = {
        id: 'name',
        header: 'COMMON.USER',
        value: (metricDataPoint) => constructNameForUser(metricDataPoint.user),
        tone: 'strong',
        sortable: true,
      }

      const organization: AppTableColumn<UsageByUserRow> = {
        id: 'organizationId',
        header: 'COMMON.ORGANIZATION',
        value: (metricDataPoint) => metricDataPoint.user.organization?.name ?? '',
        cellTemplate: this.organizationCell,
        filter: 'facet',
      }

      // Only where there is more than one environment to tell apart: naming the only one there is
      // adds a column that says the same thing on every row.
      const environment: AppTableColumn<UsageByUserRow> = {
        id: 'environment',
        header: 'COMMON.ENVIRONMENT',
        value: (metricDataPoint) => metricDataPoint.environment.label,
        cellTemplate: this.environmentCell,
        filter: 'facet',
      }

      const status: AppTableColumn<UsageByUserRow> = {
        id: 'status',
        header: 'COMMON.STATUS',
        value: (metricDataPoint) => (metricDataPoint.active ? 'ACTIVE' : 'INACTIVE'),
        chip: (metricDataPoint) => ({
          label: this.translateService.instant(metricDataPoint.active ? 'COMMON.ACTIVE' : 'COMMON.INACTIVE'),
          variant: metricDataPoint.active ? 'success' : 'neutral',
        }),
        filter: 'facet',
        facetLabel: (value) => this.translateService.instant(value === 'ACTIVE' ? 'COMMON.ACTIVE' : 'COMMON.INACTIVE'),
        defaultFacetValues: ['ACTIVE'],
        facetSearch: false,
      }

      const intervalColumns = usageData[0].usageByInterval.map(({ intervalLabel, intervalType }, index: number) => ({
        id: `interval${index}`,
        header: () => this.formatIntervalLabel(intervalLabel, intervalType),
        value: (metricDataPoint: UsageByUserRow) =>
          readUsage(viewMode === 'HOURS' ? metricDataPoint.usageByInterval[index].duration : metricDataPoint.usageByInterval[index].count),
        display: (metricDataPoint: UsageByUserRow) =>
          showUsage(viewMode === 'HOURS' ? metricDataPoint.usageByInterval[index].duration : metricDataPoint.usageByInterval[index].count),
        numeric: true,
        sortable: true,
      }))

      const total: AppTableColumn<UsageByUserRow> = {
        id: 'usageNumberTotal',
        header: 'COMMON.TOTAL',
        value: (metricDataPoint) => readUsage(viewMode === 'HOURS' ? metricDataPoint.totalDuration : metricDataPoint.totalCount),
        display: (metricDataPoint) => showUsage(viewMode === 'HOURS' ? metricDataPoint.totalDuration : metricDataPoint.totalCount),
        numeric: true,
        sortable: true,
      }

      return [name, ...(organizationIds.length > 1 ? [organization] : []), ...(this.isMultiEnvironment ? [environment] : []), status, ...intervalColumns, total]
    })
  )

  userStatsRowId = (metricDataPoint: UsageByUserRow): string => scopedId(metricDataPoint.environment, metricDataPoint.user.id)

  @ViewChild('organizationCell', { static: true }) organizationCell!: TemplateRef<{ $implicit: UsageByUserRow }>

  /**
   * One template, two handles. Both tables render the same environment pill, but a TemplateRef is
   * invariant in its context, so a single handle cannot be typed for two row types that have nothing
   * in common beyond the environment they carry.
   */
  @ViewChild('environmentCell', { static: true }) environmentCell!: TemplateRef<{ $implicit: UsageByUserRow }>

  @ViewChild('environmentCell', { static: true }) roomEnvironmentCell!: TemplateRef<{ $implicit: MeetingRoomRow }>

  canFollowSupportLinks$ = inject(SupportLinkResolver).canFollowSupportLinks()

  // Spread around the hue circle by the golden angle, so neighbouring ids do not come out alike, and
  // offset per environment so that the same id in two of them is not the same badge.
  organizationColor(environment: SupportEnvironment, organizationId: number): string {
    const hue = (organizationId * GOLDEN_ANGLE_DEGREES + environmentHue(environment, this.analyticsService.environments)) % 360
    return `hsl(${hue}, 70%, 90%)`
  }

  exclusionFiltersForCompletedCalls$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => {
      return {
        ERRORED: !(queryParamMap.get(constructQueryParameterName('exclusionFilters', this.completedCallQueryPrefix, 'ERRORED')) === 'false'),
        SHORTER_THAN_15_MIN:
          queryParamMap.get(constructQueryParameterName('exclusionFilters', this.completedCallQueryPrefix, 'SHORTER_THAN_15_MIN')) === 'true',
      }
    })
  )

  completedCalls$ = combineLatest([this._organizationIds$, this.route.queryParamMap, this.exclusionFiltersForCompletedCalls$]).pipe(
    debounceTime(300),
    map(([organizationIds, queryParamMap, exclusionFiltersRaw]) => {
      const activeViewQueryParam = constructQueryParameterName('activeView', this.completedCallQueryPrefix)
      const nameQueryParam = constructQueryParameterName('name', this.completedCallQueryPrefix)
      return {
        organizationIds,
        exclusionFilters: getActiveToggleMenuOptionIds(exclusionFiltersRaw),
        activeView: queryParamMap.has(activeViewQueryParam) ? queryParamMap.get(activeViewQueryParam) : null,
        intervalKey: (queryParamMap.get('intervalKey') as IntervalKey) ?? this.defaultTimeInterval,
        callNameQuery: queryParamMap.has(nameQueryParam) ? queryParamMap.get(nameQueryParam) : null,
        ...getPaginationOptionsFromQueryParamMap(queryParamMap, this.completedCallQueryPrefix, {
          pageSize: this.completedCallsDefaultPageSize,
          sortField: 'actualStartDate',
          sortDirection: 'desc',
        }),
      }
    }),
    // See userStats$: the request is spelled out before the dedupe so that a query parameter it has
    // nothing to do with — the table's own sorting among them — cannot re-run it.
    distinctUntilChanged((previous, current) => isEqual(previous, current)),
    switchMap((request) =>
      this.analyticsService.completedCalls(this.scope, request.organizationIds, {
        exclusionFilters: request.exclusionFilters,
        activeView: request.activeView,
        interval: convertIntervalKeyToTimeInterval(request.intervalKey),
        nameQuery: request.callNameQuery,
        sortField: request.sortField,
        sortDirection: request.sortDirection ?? 'desc',
        page: request.page,
        pageSize: request.pageSize,
      })
    ),
    shareReplay(1)
  )

  meetingRoomMetrics$ = this._organizationIds$.pipe(
    switchMap((organizationIds) => this.analyticsService.meetingRooms(this.scope, organizationIds)),
    map((result) => ({ ...loadState(result), data: result.data })),
    shareReplay(1)
  )

  audioQualityChartData = AUDIO_QUALITY_CHART_DATA

  meetingRoomScoresChart$ = combineLatest([this.meetingRoomMetrics$, this.usagesByOrganization$, this.showMeetingRoomLabels$]).pipe(
    map(([result, usagesResult, showLabels]) => {
      const rooms = result.data.filter((room) => room.averageConfidenceScore !== null)
      const scored = this.scoredByOrganization(usagesResult.data, rooms, (room) => room.averageConfidenceScore)

      return {
        options: this.buildAudioQualityChartOptions(),
        dotsPlugin: createDotsPlugin(
          rooms.map((room) => ({ name: room.canonicalName, score: room.averageConfidenceScore!, color: scored.colorOf(room) })),
          { showLabels }
        ),
        legend: scored.legend,
      }
    }),
    shareReplay(1)
  )

  callScoresChart$ = combineLatest([this.completedCalls$, this.usagesByOrganization$, this.showCallLabels$]).pipe(
    map(([callsResult, usagesResult, showLabels]) => {
      const calls = callsResult.data.filter((call) => call.avgConfidenceScore !== null)
      const scored = this.scoredByOrganization(usagesResult.data, calls, (call) => call.avgConfidenceScore)

      return {
        options: this.buildAudioQualityChartOptions(),
        dotsPlugin: createDotsPlugin(
          calls.map((call) => ({ name: call.name, score: call.avgConfidenceScore!, color: scored.colorOf(call) })),
          { dotRadius: 5, showLabels }
        ),
        legend: scored.legend,
      }
    }),
    shareReplay(1)
  )

  // The colour each scored row is drawn in, and the per-organization averages beside the chart.

  private scoredByOrganization<T extends { environment: SupportEnvironment; organizationId: number }>(
    organizations: UsageByOrganizationRow[],
    rows: T[],
    scoreOf: (row: T) => number | null
  ) {
    const colorPalette = getColorPalette(organizations.length)
    // Scoped, because the palette is handed out per merged row and two environments both having an
    // organization 12 would otherwise share a colour and be averaged together.
    const keyOf = (row: T) => scopedId(row.environment, row.organizationId)
    const keyOfOrganization = (row: UsageByOrganizationRow) => scopedId(row.environment, row.organization.id)
    const colorByKey = Object.fromEntries(organizations.map((organization, index) => [keyOfOrganization(organization), colorPalette[index]]))
    const averageLabel = this.translateService.instant('METRICS.ANALYTICS_DASHBOARD.AVG_AUDIO_SCORE')

    return {
      colorOf: (row: T) => colorByKey[keyOf(row)] ?? UNKNOWN_ORGANIZATION_COLOR,
      legend:
        organizations.length > 1
          ? organizations.map((organization, index) => {
              const scores = rows
                .filter((row) => keyOf(row) === keyOfOrganization(organization))
                .map(scoreOf)
                .filter((score): score is number => score !== null)
              const average = scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null
              const name = qualifiedByEnvironment(organization.environment, organization.organization.name)
              return { name: average !== null ? `${name} (${averageLabel}: ${average.toFixed(2)})` : name, color: colorPalette[index] }
            })
          : [],
    }
  }

  private buildAudioQualityChartOptions(): ChartConfiguration<'bar'>['options'] {
    const thresholdAnnotation = {
      type: 'line' as const,
      xMin: 5.5,
      xMax: 5.5,
      borderColor: '#017101',
      borderWidth: 3,
      borderDash: [6, 4],
      label: {
        display: true,
        content: this.translateService.instant('METRICS.ANALYTICS_DASHBOARD.SATISFACTORY_THRESHOLD'),
        position: 'start' as const,
        backgroundColor: 'transparent',
        color: '#017101',
        font: { size: 14 },
        yAdjust: -400,
        xAdjust: 140,
      },
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 80,
          left: 20,
          right: 20,
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: this.translateService.instant('METRICS.ANALYTICS_DASHBOARD.AUDIO_QUALITY_SCORE_AXIS'),
            font: { size: 15 },
            color: '#666',
          },
          grid: { display: false },
          offset: true,
          ticks: {
            font: { size: 12 },
            color: '#666',
            labelOffset: -50,
          },
        },
        y: {
          grid: {
            display: true,
            color: '#E5E7EB',
            drawTicks: false,
            drawOnChartArea: true,
          },
          border: {
            display: false,
          },
          ticks: {
            display: false,
            maxTicksLimit: 4,
          },
          beginAtZero: true,
        },
        yRight: {
          position: 'right' as const,
          title: {
            display: true,
            text: this.translateService.instant('METRICS.ANALYTICS_DASHBOARD.NUMBER_OF_CALLS_AXIS'),
            font: { size: 15 },
            color: '#666',
          },
          ticks: { display: false },
          grid: { display: false },
          border: {
            display: false,
          },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
        annotation: {
          annotations: {
            threshold: thresholdAnnotation,
          },
        },
      },
    }
  }

  chartDataForOrganizationUsage$ = combineLatest([this.usagesByOrganization$, this.usageStatsSelectedView$]).pipe(
    map(([result, viewOption]) => this.transformToChartData(result.data, viewOption === 'HOURS' ? 'HOURS' : 'COUNTS')),
    shareReplay(1)
  )

  barChartOptions$: Observable<ChartConfiguration<'bar'>['options']> = this._organizationIds$.pipe(
    map((organizationIds) => {
      const showLegend = organizationIds.length > 1

      return {
        responsive: true,
        layout: {
          padding: {
            top: 20,
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
          },
          y: { stacked: true },
        },
        plugins: {
          legend: {
            display: showLegend,
            position: 'bottom',
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const label = context.dataset.label ?? ''
                const value = context.raw
                return `${label}: ${Math.round(value as number)}`
              },
            },
          },
          datalabels: {
            anchor: 'end',
            align: 'top',
            color: '#000000',
            font: {
              weight: 'bold',
              size: 12,
            },
            formatter: (value: number, ctx) => {
              const datasets = ctx.chart.data.datasets
              let total = 0
              datasets.forEach((d) => {
                total += (d.data[ctx.dataIndex] as number) || 0
              })

              return ctx.datasetIndex === datasets.length - 1 && total > 0 ? total.toFixed(0) : ''
            },
          },
        },
      } satisfies ChartConfiguration<'bar'>['options']
    }),
    shareReplay(1)
  )

  usageStatsViewOptions: ButtonToggleOption[] = [
    {
      id: 'HOURS',
      labelTranslationKey: 'ADMIN.VIEW_OPTIONS.HOURS',
    },
    {
      id: 'COUNTS',
      labelTranslationKey: 'ADMIN.VIEW_OPTIONS.COUNTS',
    },
  ]

  updateSelectedView(value: string | null, prefix: string) {
    this.router.navigate([], {
      queryParams: {
        [constructQueryParameterName('activeView', prefix)]: value,
      },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  meetingRoomColumns: AppTableColumn<MeetingRoomRow>[] = []

  private buildMeetingRoomColumns(): AppTableColumn<MeetingRoomRow>[] {
    const environment: AppTableColumn<MeetingRoomRow> = {
      id: 'environment',
      header: 'COMMON.ENVIRONMENT',
      value: (room) => room.environment.label,
      cellTemplate: this.roomEnvironmentCell,
      filter: 'facet',
    }
    return [
      { id: 'canonicalName', header: 'METRICS.ANALYTICS_DASHBOARD.ROOM_NAME', value: (room) => room.canonicalName, tone: 'strong', sortable: true },
      ...(this.isMultiEnvironment ? [environment] : []),
      {
        id: 'averageConfidenceScore',
        header: 'METRICS.ANALYTICS_DASHBOARD.CONFIDENCE_SCORE',
        value: (room) => room.averageConfidenceScore ?? -1,
        display: (room) => room.averageConfidenceScore?.toFixed(2) ?? '-',
        numeric: true,
        sortable: true,
      },
      {
        id: 'averageRms',
        header: 'METRICS.ANALYTICS_DASHBOARD.RMS',
        value: (room) => room.averageRms ?? -1,
        display: (room) => room.averageRms?.toFixed(2) ?? '-',
        numeric: true,
        sortable: true,
      },
      {
        id: 'averageHfRatio',
        header: 'METRICS.ANALYTICS_DASHBOARD.HF_RATIO',
        value: (room) => room.averageHfRatio ?? -1,
        display: (room) => room.averageHfRatio?.toFixed(2) ?? '-',
        numeric: true,
        sortable: true,
      },
      { id: 'callCount', header: 'METRICS.ANALYTICS_DASHBOARD.CALL_COUNT', value: (room) => room.callCount, numeric: true, sortable: true },
    ]
  }

  meetingRoomRowId = (room: MeetingRoomRow): string => scopedId(room.environment, room.id)

  transformToChartData(usages: UsageByOrganizationRow[], viewOption: 'HOURS' | 'COUNTS'): ChartConfiguration<'bar'>['data'] {
    const labels =
      usages[0]?.usageByInterval.map((usage) => {
        return this.formatIntervalLabel(usage.intervalLabel, usage.intervalType)
      }) ?? []
    const colorPalette = getColorPalette(usages.length)

    const datasets = usages.map((usage, index) => ({
      label: qualifiedByEnvironment(usage.environment, usage.organization.name),
      data: usage.usageByInterval.map((item) => (viewOption === 'HOURS' && item.duration !== null ? item.duration / 3600 : item.count)),
      backgroundColor: colorPalette[index],
    }))
    return {
      labels,
      datasets,
    }
  }

  updateIntervalKey(intervalKey: IntervalKey) {
    this.router.navigate([], {
      queryParams: { ['intervalKey']: intervalKey },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  formatIntervalLabel(intervalLabel: string, intervalType: 'DAY' | 'MONTH'): string {
    if (intervalType === 'MONTH') {
      // intervalLabel is "YYYY-MM"
      const date = new Date(`${intervalLabel}-01`)
      const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase() // "SEPTEMBER"
      return this.translateService.instant(`COMMON.MONTHS.${month}`)
    }

    if (intervalType === 'DAY') {
      // intervalLabel is "YYYY-MM-DD"
      const date = new Date(intervalLabel)
      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
      }).format(date)
    }

    return intervalLabel
  }
}
